import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Chart,
    ColumnSeries,
    HighchartsChart,
    Legend,
    LineSeries,
    Tooltip,
    withHighcharts,
    XAxis,
    YAxis,
} from 'react-jsx-highcharts';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import debounce from 'lodash/debounce';
import { Translate, withLocalize } from 'react-localize-redux';
import moment from 'moment';
import { Tooltip as Tippy } from 'react-tippy';
import css from './LineChart.css';
import Toggle from '../atoms/Toggle';
import Spinner from '../widgets/Spinner';
import { isFullSpanPeriod, normalizeTimeBreakDown } from '../../utils/regional';
import { colorLuminance } from '../../utils/colors';
import { fonts, generateArrayOfSize } from '../../global/utils';
import PlusIcon from '../../resources/svg/icon_chart_add_metric.svg';
import MinusIcon from '../../resources/svg/icon_chart_remove_metric.svg';
import { Dropdown, EmptyState } from '../widgets';
import { REPORTS_EVENT_PREFIX, trackMixpanelEvent } from '../../utils/general';

import {
    dashStyles,
    dashStylesCSS,
    getColorByIndex,
    legendFormatter,
    metricsPriority,
    tooltipFormatter,
    tooltipPositioner,
    valueFormatter,
} from '../../utils/charts';

Highcharts.setOptions({
    lang: {
        thousandsSep: ',',
        resetZoom: 'Reset Zoom',
    },
});

const isVisible = item => {
    return !('visible' in item) || item.visible;
};

const colors = {
    text: '#94A3B8',
    secondaryText: '#94A3B8',
    textLighter: '#CCCCCC',
    darkText: '#334155',
    axis: '#94A3B8',
    title: '#777D84',
    background: '#FFFFFF',
    tooltipBackground: '#FFFFFF',
    primary: '#00a1ff',
    inactive: '#E2E8F0',
};

let breakdown;

const formatSingleXlabel = (value, isFirst, axis) => {
    const date = moment(value, 'YYYY-MM-DD');
    let ret = date.date();
    if (isFirst) {
        ret = `${ret}<br/>${date.format('MMMM')}`;
    } else {
        const index = axis.categories.indexOf(value);
        const prev = axis.categories[index - 1];
        const prevMonth = moment(prev).month();
        const currentMonth = moment(value).month();
        if (prevMonth !== currentMonth) {
            ret = `${ret}<br/>${date.format('MMMM')}`;
        }
    }
    return ret;
};

const styles = {
    metricTooltip: {
        style: {
            boxShadow: 'rgba(68, 75, 87, 0.25) -2px 2px 12px 0px',
            border: 'none',
            marginTop: '-3px',
            fontSize: '14px',
            fontWeight: 300,
            color: '#000000',
            borderRadius: '3px',
            cursor: 'default',
        },
    },
    highchartsChart: {
        plotOptions: {
            column: {
                stacking: 'normal',
                brightness: 0.3,
                tooltip: {
                    distance: 16,
                },
            },
            series: {
                events: {
                    mouseOver() {
                        trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Hover on point');
                    },
                },
            },
        },
    },
    chart: {
        style: {
            fontFamily: fonts,
        },
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 7,
        alignTicks: true,
        backgroundColor: colors.background,
        responsive: {
            rules: [],
        },
    },
    legend: {
        itemStyle: {
            color: colors.secondaryText,
            fontSize: '14px',
            fontWeight: '400',
        },
        itemHiddenStyle: {
            color: colors.textLighter,
        },
        navigation: {
            activeColor: colors.primary,
            inactiveColor: colors.inactive,
        },
        floating: false,
        itemMarginBottom: 10,
        backgroundColor: colors.background,
        align: 'right',
        verticalAlign: 'middle',
        x: 0,
        y: 0,
        padding: 10,
        layout: 'vertical',
        useHTML: true,
        labelFormatter: legendFormatter(css),
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        symbolHeight: 0.001,
        symbolPadding: 0,
        squareSymbol: false,
        maxHeight: 280,
    },
    tooltip: {
        backgroundColor: colors.tooltipBackground,
        shadow: false,
        style: {
            boxShadow: '0 0 3px 1px rgba(0,0,0,0.2)',
        },
        formatter: tooltipFormatter(css, breakdown),
        useHTML: true,
        padding: 0,
        borderWidth: 0,
        shared: true,
        positioner: tooltipPositioner,
    },
    title: {
        style: {
            color: colors.darkText,
            fontSize: '16px',
            fontWeight: 500,
            maxWidth: '340px',
            whiteSpace: 'normal',
        },
        align: 'left',
        margin: 40,
        x: 0,
        useHTML: true,
    },
    xAxis: {
        tickLength: 0,
        labels: {
            style: {
                color: colors.text,
                fontSize: '12px',
                fontWeight: 400,
                textAlign: 'right',
            },
            formatter() {
                if (this.value && this.value.includes('...')) {
                    const split = this.value.split(' ... ');
                    return `<div class="${css.xLabelHolder} ${
                        isFullSpanPeriod(breakdown, split[0], split[1]) ? '' : css.partial
                    }">
                                <span class="${css.xLabelItem}">
                                    ${formatSingleXlabel(split[0], true, this.axis)}
                                </span>
                                <span class="${css.xLabelItem}" style="margin: 0 2px"> - </span> 
                                <span class="${css.xLabelItem}">
                                    ${formatSingleXlabel(split[1], true, this.axis)}
                                </span>
                            </div>`;
                }
                this.breakdown = breakdown;
                return formatSingleXlabel(this.value, this.isFirst, this.axis);
            },
            overflow: 'justify',
            useHTML: true,
        },
        tickmarkPlacement: 'on',
        crosshair: {
            color: '#CCCCCC',
            dashStyle: 'Solid',
            snap: true,
            width: 1,
            zIndex: 2,
        },
        lineColor: '#E2E8F0',
    },
    yAxis: {
        gridLineColor: '#EBECED',
    },
    types: {
        line: {
            classType: LineSeries,
            markerEnabled: true,
            props: {
                zIndex: 2,
                animation: {
                    duration: 500,
                },
            },
            brightness: 0,
        },
        bar: {
            classType: ColumnSeries,
            markerEnabled: false,
            props: {
                zIndex: 1,
                maxPointWidth: 100,
                animation: false,
                stacking: 'normal',
            },
            dashStyle: dashStyles[0],
            dashStyleCSS: dashStylesCSS[0],
            brightness: 0.1,
        },
    },
};

const LineChart = ({
    chartData = {},
    chartData: { orgOptions = {} } = {},
    chart: { showError: storeShowError } = { showError: false },
    translate = str => str,
    className = '',
    showTopLinesInput = true,
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    numOfTableRows = 0,
    spinnerStyle = {},
    timeBreakdown = 'day',
    chart = {},
    onChartLegendToggle = () => {},
    onMetricSelected = () => {},
    onChartLoaded = () => {},
    onLinesChanged = () => {},
    showLegend: showLegendProp = false,
}) => {
    const midContainerRef = useRef(null);
    const metricsBarRef = useRef(null);
    const hiddenItemsRef = useRef(new Set());
    const loadedReportedRef = useRef(false);
    const metricsLimitReachedRef = useRef(false);

    const [lines, setLines] = useState(chart.lines);
    const linesRef = useRef(lines);
    useEffect(() => {
        linesRef.current = lines;
    }, [lines]);

    useEffect(() => {
        breakdown = timeBreakdown;
        styles.tooltip.formatter = tooltipFormatter(css, breakdown);
    }, [timeBreakdown]);

    const isAlone = useCallback((data, index) => {
        return (!data[index + 1] || !data[index + 1].y) && (!data[index - 1] || !data[index - 1].y);
    }, []);

    const getDateKeysFromMetricData = useCallback((metricName, dataSource) => {
        return Object.keys(dataSource[metricName] || {}).sort();
    }, []);

    const getLines = useCallback(
        metric => {
            const itemsMap = {};
            const dates = getDateKeysFromMetricData(metric.name, chartData);

            if (!dates.length) {
                return [
                    {
                        id: `fake_${metric.name}`,
                        name: metric.name,
                        hash: `fake_${metric.name}`,
                        data: ['N/A'],
                        formatter: valueFormatter(metric),
                    },
                ];
            }
            const dimensionKeys = Object.keys(chartData.dimensions || {});
            let counter = 0;

            for (const date of dates) {
                const items = chartData[metric.name][date];

                for (const dim of dimensionKeys) {
                    const itemValue = items && items[dim] && items[dim] !== 'N/A' ? items[dim] : null;
                    const itemTrans = chartData.dimensions[dim];

                    if (!itemsMap[dim]) {
                        itemsMap[dim] = {};
                    }

                    if (!itemsMap[dim].data) {
                        itemsMap[dim].data = [];
                    }

                    itemsMap[dim].id = `${dim}_${metric.name}`;
                    itemsMap[dim].name = itemTrans;
                    itemsMap[dim].hash = dim;

                    if (!itemsMap[dim].xValue) itemsMap[dim].xValue = date;

                    itemsMap[dim].data.push({
                        y: itemValue,
                        name: `item_${dim}_${counter}`,
                    });
                    itemsMap[dim].formatter = valueFormatter(metric);
                }
                counter++;
            }

            return Object.values(itemsMap).map(item => {
                item.data = item.data.map((dataObj, index) => {
                    if (dataObj && isAlone(item.data, index)) {
                        dataObj.marker = { enabled: true };
                    }
                    return dataObj;
                });
                return item;
            });
        },
        [chartData, getDateKeysFromMetricData, isAlone]
    );

    const calculatedDataLength = useCallback(metricsList => {
        return metricsList
            .map(metric => metric.lines)
            .reduce((total, item) => total.concat(item), [])
            .reduce((total, item) => total.concat(item.data), [])
            .filter(item => item && item.y !== 'N/A' && item.y !== null).length;
    }, []);

    const getXAxis = useCallback(
        (metricsList, inProps) => {
            const { chartData: cd } = inProps || { chartData, chart };
            const metric = metricsList[0];
            return metric?.name ? getDateKeysFromMetricData(metric.name, cd) : [];
        },
        [getDateKeysFromMetricData, chartData, chart]
    );

    const normalizeMetrics = useCallback(
        inProps => {
            const {
                chartData: cd,
                chart: ch = {},
                chartData: { orgOptions: orgOpts = {} } = {},
            } = inProps || { chartData, chart };
            const selectedMetrics = ch.selectedMetrics || [];
            let selectedCount = 0;
            const metricsLimit = orgOpts.metricsLimit || 5;
            const numberOfSelectedMetricsOnInit = Math.max(metricsLimit - 1, 1);

            const mappedMetrics = cd.metrics.map(metric => {
                const metricCopy = { ...metric };
                metricCopy.lines = getLines(metricCopy);
                metricCopy.empty = !calculatedDataLength([metricCopy]);
                metricCopy.formatter = valueFormatter(metricCopy);
                metricCopy.yAxisFormatter = valueFormatter(metricCopy, true);
                metricCopy.shown = false;
                metricCopy.visible = selectedMetrics.includes(metricCopy.name) && !metricCopy.empty;
                metricCopy.priority = metricsPriority.includes(metricCopy.name)
                    ? metricsPriority.indexOf(metricCopy.name)
                    : metricsPriority.length;

                if (metricCopy.visible) {
                    selectedCount++;
                }

                return metricCopy;
            });

            const metricsToToggle = Math.max(numberOfSelectedMetricsOnInit - selectedCount, 0);
            mappedMetrics
                .filter(m => !m.empty && !m.visible)
                .sort((a, b) => a.priority - b.priority)
                .slice(0, metricsToToggle)
                .forEach(m => {
                    m.visible = true;
                });
            return mappedMetrics;
        },
        [calculatedDataLength, getLines, chartData, chart]
    );

    const [{ metrics, xData }, setChartData] = useState(() => {
        const normalizedMetrics = normalizeMetrics();
        return {
            metrics: normalizedMetrics,
            xData: getXAxis(normalizedMetrics),
        };
    });
    const [showLegend, setShowLegend] = useState(showLegendProp);

    const prevPropsRef = useRef({ chartData, timeBreakdown });
    useEffect(() => {
        const prev = prevPropsRef.current;
        const normalizedMetrics = normalizeMetrics();
        const currentKeys = Object.keys(prev.chartData || {});
        const nextKeys = Object.keys(chartData || {});
        const currentXAxisLen = xData.length;
        const nextXAxis = getXAxis(normalizedMetrics);
        const currentDimensions =
            prev.chartData && prev.chartData.dimensions ? Object.keys(prev.chartData.dimensions) : [];
        const nextDimensions = chartData && chartData.dimensions ? Object.keys(chartData.dimensions) : [];

        if (
            currentKeys.length !== nextKeys.length ||
            currentXAxisLen !== nextXAxis.length ||
            currentDimensions.length !== nextDimensions.length
        ) {
            hiddenItemsRef.current = new Set();
            loadedReportedRef.current = false;
            setChartData({
                metrics: normalizedMetrics,
                xData: nextXAxis,
            });
        }

        prevPropsRef.current = { chartData, timeBreakdown };
    }, [chartData, timeBreakdown, getXAxis, normalizeMetrics]);

    const setMidContainerMinWidth = useCallback(() => {
        const metricsBar = metricsBarRef.current;
        const midContainer = midContainerRef.current;
        if (!metricsBar || !metricsBar.children || !metricsBar.children.length || !midContainer) {
            return;
        }
        const first = metricsBar.children[0].getBoundingClientRect();
        const last = metricsBar.children[Math.min(metricsBar.children.length - 1, 5)].getBoundingClientRect();
        const minWidth = Math.max(last.left - first.left + last.width + 48, 650);
        midContainer.style.minWidth = `${minWidth}px`;
    }, []);
    useEffect(() => {
        setMidContainerMinWidth();
    }, [setMidContainerMinWidth, metrics, xData, lines, showLegend]);

    const updateLinesChanged = useMemo(
        () =>
            debounce(
                () => {
                    onLinesChanged(linesRef.current);
                },
                1000,
                { trailing: true }
            ),
        [onLinesChanged]
    );
    useEffect(() => {
        return () => {
            updateLinesChanged.cancel();
        };
    }, [updateLinesChanged]);

    const visibleMetrics = useMemo(() => metrics.filter(metric => isVisible(metric)), [metrics]);
    const showError = !calculatedDataLength(visibleMetrics) || storeShowError;
    const showSpinner = !metrics.length && !storeShowError;

    const getTitle = useCallback(() => {
        const visible = metrics.filter(metric => isVisible(metric));
        if (!visible || !visible.length) {
            return '';
        }
        let ret = visible.reduce((total, current, index) => {
            let separator = ', ';
            if (index === visible.length - 1) {
                separator = '';
            }
            return `${total} ${current.display_name}${separator} `;
        }, '');
        ret = ret.replace(/,([^,]*)$/, ' and$1');
        ret = (
            <Translate
                id="STATIC.REPORT_CHART_TITLE_2"
                data={{
                    metrics: ret,
                    time: normalizeTimeBreakDown(timeBreakdown),
                }}
            />
        );
        return ret;
    }, [metrics, timeBreakdown]);
    const getChartParams = useCallback(() => {
        return {
            title: getTitle(),
            metrics: metrics.map(({ name, display_name, format, shown, visible, empty }) => {
                return {
                    name,
                    display_name,
                    format,
                    shown,
                    visible,
                    empty,
                };
            }),
            num_ref_rows: lines,
            raw_data: chartData,
        };
    }, [metrics, lines, chartData]);

    useEffect(() => {
        if (!showSpinner && !loadedReportedRef.current) {
            loadedReportedRef.current = true;
            setTimeout(() => {
                onChartLoaded(getChartParams());
            });
        }
    }, [showSpinner, onChartLoaded, getChartParams]);

    const toggleLegend = useCallback(() => {
        setShowLegend(prev => !prev);
        requestAnimationFrame(() => {
            onChartLegendToggle();
        });
    }, [onChartLegendToggle]);

    const isMetricSelected = useCallback(
        cMetric => {
            const foundMetric = metrics.find(metric => cMetric.name === metric.name);
            return foundMetric && isVisible(foundMetric);
        },
        [metrics]
    );

    const getAxisOptions = useCallback(
        metric => {
            const chartType = metric.chart_type || 'line';
            const visible = metrics.filter(cMetric => isVisible(cMetric));
            const visibleLine = visible.filter(cMetric => !cMetric.chart_type || cMetric.chart_type === 'line');
            const metricIndex = visible.findIndex(cMetric => cMetric.name === metric.name);
            const metricLineIndex = visibleLine.findIndex(cMetric => cMetric.name === metric.name);
            const seriesStyle = styles.types[chartType];
            return {
                dashStyle: seriesStyle.dashStyle || dashStyles[metricLineIndex],
                dashStyleCSS: seriesStyle.dashStyleCSS || dashStylesCSS[metricLineIndex],
                opposite: metricIndex % 2 === 1,
                first: visible[0],
                currentIndex: metricIndex,
                seriesStyle,
            };
        },
        [metrics]
    );

    const getTopLines = useCallback(() => {
        if (chartData.dimensionsSorted === undefined) {
            return Object.keys(chartData.dimensions);
        }
        return chartData.dimensionsSorted.slice(0, lines);
    }, [chartData, lines]);

    const handleLinesChanged = useCallback(
        val => {
            let value = val;
            try {
                value = parseInt(value, 10);
            } catch (e) {}
            if (typeof value !== 'number' || value > 15 || value < 1) {
                return;
            }
            setLines(val);
            updateLinesChanged();
        },
        [updateLinesChanged]
    );

    const setCurrentMetric = useCallback(
        (metric, icon) => {
            const newMetrics = [...metrics];
            const metricIndex = newMetrics.findIndex(cMetric => cMetric.name === metric.name);
            const visible = newMetrics.filter(cMetric => isVisible(cMetric));
            if (isVisible(newMetrics[metricIndex]) && visible.length === 1) {
                return;
            }
            if (
                isVisible(newMetrics[metricIndex]) ||
                orgOptions.metricsLimit === 1 ||
                visible.length < orgOptions.metricsLimit
            ) {
                if (!icon || orgOptions.metricsLimit === 1) {
                    for (let i = 0; i < visible.length; i++) {
                        const metricCopy = { ...visible[i] };
                        metricCopy.visible = false;
                        const visibleMetricIndex = newMetrics.findIndex(cMetric => cMetric.name === metricCopy.name);
                        newMetrics.splice(visibleMetricIndex, 1, metricCopy);
                    }
                }
                const metricCopy = { ...newMetrics[metricIndex] };
                metricCopy.visible = !metricCopy.visible;
                newMetrics.splice(metricIndex, 1, metricCopy);
                metricsLimitReachedRef.current =
                    newMetrics.filter(cMetric => isVisible(cMetric)).length >= orgOptions.metricsLimit;
                setTimeout(() => {
                    onMetricSelected(metricCopy, getChartParams());
                }, 50);

                setChartData(prev => ({ ...prev, metrics: newMetrics }));
            }
        },
        [metrics, orgOptions.metricsLimit, onMetricSelected, getChartParams]
    );

    const renderMetrics = useMemo(() => metrics.filter(metric => isVisible(metric) || metric.shown), [metrics]);
    const renderLabels = getTopLines();

    return (
        <div className={`${css.container} ${className}`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <div
                className={css.midContainer}
                ref={el => {
                    midContainerRef.current = el;
                }}
            >
                <Spinner expanded show={showSpinner} style={spinnerStyle} />
                {showSpinner ? <div className={css.metricsBarPlaceholder} /> : null}
                {!showSpinner
                    ? [
                          <div
                            key="chart_metrics_bar"
                            className={`${css.metricsbar} ${metrics.length === 0 ? css.noMetrics : ''}`}
                            ref={el => {
                                  metricsBarRef.current = el;
                              }}
                          >
                            {metrics.map(cMetric => {
                                  const isSelected = isMetricSelected(cMetric);
                                  const options = getAxisOptions(cMetric);
                                  const { empty } = cMetric;
                                  return (
                                      <button
                                          key={cMetric.name}
                                          onClick={() => {
                                              if (empty) {
                                                  return;
                                              }
                                              setCurrentMetric(cMetric, true);
                                          }}
                                          className={`${css.metricButton} ${isSelected ? css.selected : ''} ${
                                              empty ? css.disabled : ''
                                          }`}
                                      >
                                          <div className={css.metricButtonIcon}>
                                              {(() => {
                                                  const visible = metrics.filter(m => isVisible(m));
                                                  if (orgOptions.metricsLimit === 1 || empty) {
                                                      return null;
                                                  } else if (visible.length === orgOptions.metricsLimit) {
                                                      return isSelected ? <MinusIcon /> : null;
                                                  } else if (visible.length <= 1) {
                                                      return isSelected ? null : <PlusIcon />;
                                                  } else if (visible.length > 1) {
                                                      return isSelected ? <MinusIcon /> : <PlusIcon />;
                                                  }
                                                  return null;
                                              })()}
                                          </div>
                                          <Tippy
                                              title={translate(
                                                  empty
                                                      ? 'STATIC.PAGES.REPORTS.CHART_METRIC_NO_DATA'
                                                      : 'STATIC.PAGES.REPORTS.CHART_MAX_REACHED',
                                                  { max: chartData.orgOptions.metricsLimit }
                                              )}
                                              position="bottom"
                                              popperOptions={{
                                                  modifiers: {
                                                      preventOverflow: { enabled: false },
                                                      hide: { enabled: false },
                                                  },
                                              }}
                                              inertia
                                              animation="scale"
                                              distance={10}
                                              theme="light"
                                              size="big"
                                              trigger={empty ? 'mouseenter click' : 'click'}
                                              disabled={(!empty && !metricsLimitReachedRef.current) || isSelected}
                                          >
                                              <span className={css.metricButtonLabel}>{cMetric.display_name}</span>
                                          </Tippy>
                                          {isSelected ? (
                                              <div className={css.metricButtonBorder}>
                                                  <svg style={{ width: '100%', height: '3px' }}>
                                                      <line
                                                          x1="0%"
                                                          y1="0%"
                                                          x2="100%"
                                                          y2="0%"
                                                          style={{
                                                              stroke: colors.primary,
                                                              strokeWidth: 6,
                                                              strokeDasharray: options.dashStyleCSS,
                                                          }}
                                                      />
                                                  </svg>
                                              </div>
                                          ) : null}
                                      </button>
                                  );
                              })}
                        </div>,
                          !showError ? (
                              <div className={css.topbarContainer} key="chart_topbar_container">
                                  <div className={css.topbarTitle}>{getTitle()}</div>
                                  <div className={css.topbarItems}>
                                      {showTopLinesInput && (
                                          <Fragment>
                                              <div className={css.numberOfLinesContainer}>
                                                  <span className={css.numberOfLinesLabel}>
                                                      <Translate id="STATIC.SHOW_CHART_TOP_ROWS3" />
                                                  </span>
                                                  <Dropdown
                                                      containerClass={css.numberOfLinesDropdown}
                                                      popperClass={css.numberOfLinesDropdown}
                                                      selectedContainer={css.dropdownLabel}
                                                      selected={{
                                                          name: Math.min(lines, numOfTableRows),
                                                          display_name: Math.min(lines, numOfTableRows),
                                                      }}
                                                      onSelection={s => {
                                                          handleLinesChanged(s.name);
                                                      }}
                                                      items={generateArrayOfSize(Math.min(15, numOfTableRows)).map(
                                                          i => {
                                                              return { name: i + 1, display_name: i + 1 };
                                                          }
                                                      )}
                                                  />
                                              </div>
                                              <span className={css.spacerVertical} />
                                          </Fragment>
                                      )}
                                      <Toggle
                                          label={<Translate id="STATIC.BUTTONS.LEGEND_TOGGLE" />}
                                          onToggle={toggleLegend}
                                          style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                          labelStyle={{
                                              color: colors.darkText,
                                              fontWeight: 300,
                                              fontSize: '15px',
                                              margin: '2px 0 0 0',
                                          }}
                                          iconStyle={{ paddingLeft: 0 }}
                                          checked={showLegend && !showError}
                                          disabled={showError}
                                      />
                                  </div>
                              </div>
                          ) : null,
                      ]
                    : null}
                <HighchartsChart className={css.highcartsContainer} {...styles.highchartsChart}>
                    {showError && !showSpinner ? (
                        <div className={css.noDataContainer}>
                            <EmptyState icon="noChartData" header="STATIC.PAGES.REPORTS.CHART_NO_DATA" />
                        </div>
                    ) : null}

                    <Chart {...styles.chart} />

                    {!showSpinner
                        ? [
                              <XAxis key="chart_xAxis" categories={xData} max={xData.length - 1} {...styles.xAxis} />,
                              <Legend {...styles.legend} key="chart_legend" enabled={showLegend && !showError} />,
                          ]
                        : null}

                    <Tooltip {...styles.tooltip} />

                    {renderMetrics.map(metric => {
                        metric.shown = true;
                        const metricLines = isVisible(metric)
                            ? metric.lines
                                  .filter(({ hash }) => renderLabels.includes(hash))
                                  .sort((a, b) => a.hash - b.hash)
                            : [];
                        const options = getAxisOptions(metric);
                        return (
                            <YAxis
                                key={metric.name}
                                id={metric.display_name}
                                labels={{
                                    style: {
                                        color: colors.text,
                                        fontSize: '12px',
                                        fontWeight: 400,
                                    },
                                    formatter: metric.yAxisFormatter,
                                }}
                                opposite={options.opposite}
                                title={{
                                    text: isVisible(metric) && visibleMetrics.length > 1 ? metric.display_name : null,
                                    style: {
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        color: colors.darkText,
                                    },
                                }}
                                {...styles.yAxis}
                            >
                                {metricLines.map((line, lineIndex) => {
                                    const seriesBrightness =
                                        options.seriesStyle.brightness + Math.floor(lineIndex / 10) * 0.3;
                                    const seriesColor = colorLuminance(getColorByIndex(lineIndex), seriesBrightness);
                                    return (
                                        <options.seriesStyle.classType
                                            id={line.id}
                                            key={line.id}
                                            stack={`${line.xValue}_${metric.name}`}
                                            marker={{
                                                enabled: line.data.length <= 1,
                                                symbol: 'circle',
                                                fillColor: '#ffffff',
                                                lineColor: null,
                                                lineWidth: 1,
                                                radius: options.seriesStyle.markerEnabled ? 2 : 0,
                                            }}
                                            states={{
                                                hover: {
                                                    enabled: true,
                                                    halo: {
                                                        size: 1,
                                                    },
                                                    lineWidthPlus: 0,
                                                },
                                            }}
                                            shadow={{
                                                color: '#F7F7F7',
                                                offsetX: 0,
                                                offsetY: 0,
                                                width: 3,
                                                opacity: 0.7,
                                            }}
                                            linkedTo={
                                                options.currentIndex !== 0
                                                    ? `${line.hash}_${options.first.name}`
                                                    : undefined
                                            }
                                            dashStyle={options.dashStyle}
                                            color={seriesColor}
                                            visible={isVisible(metric) && !hiddenItemsRef.current.has(line.hash)}
                                            events={{
                                                legendItemClick(e) {
                                                    if (this.visible) {
                                                        const points = this.chart.series;
                                                        const visPoint = points.filter(point => point.visible).length;
                                                        if (visPoint <= visibleMetrics.length) {
                                                            e.preventDefault();
                                                        }
                                                    }
                                                },
                                                hide: () => {
                                                    hiddenItemsRef.current.add(line.hash);
                                                },
                                                show: () => {
                                                    hiddenItemsRef.current.delete(line.hash);
                                                },
                                            }}
                                            {...options.seriesStyle.props}
                                            {...line}
                                        />
                                    );
                                })}
                            </YAxis>
                        );
                    })}
                </HighchartsChart>
            </div>
        </div>
    );
};

LineChart.propTypes = {
    showLegend: PropTypes.bool,
    timeBreakdown: PropTypes.string,
    onLinesChanged: PropTypes.func,
    onChartLegendToggle: PropTypes.func,
    onMetricSelected: PropTypes.func,
    onChartLoaded: PropTypes.func,
    chartData: PropTypes.objectOf(PropTypes.any),
    chart: PropTypes.objectOf(PropTypes.any),
    translate: PropTypes.func,
    className: PropTypes.string,
    showTopLinesInput: PropTypes.bool,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    numOfTableRows: PropTypes.number,
    spinnerStyle: PropTypes.objectOf(PropTypes.any),
};

export default withHighcharts(withLocalize(LineChart), Highcharts);
